import { defineStore } from 'pinia';

export interface CartItem {
  productId: string;
  // The specific marketplace offer this line is for. Different offers of the
  // same product are separate cart lines (different sellers, different prices).
  // Optional for backward-compat with cart entries saved before per-offer
  // purchasing — those continue to dedupe by productId alone.
  offerId?: string;
  merchantName?: string;
  name: string;
  priceCents: number;
  coverUrl: string | null;
  imageUrl: string | null;
  platform: string | null;
  // Max units we can buy of this SKU — copied from the offer's available qty
  // (or product.totalQty when no offer was chosen) at the time of add.
  maxQty: number;
  qty: number;
}

interface State {
  items: CartItem[];
  // null = anonymous (not yet authenticated) cart bucket. Each user gets their
  // own bucket so switching accounts never leaks a previous user's cart.
  ownerId: string | null;
  // Tracks whether `setOwner` has run at least once; without this a
  // first-time `setOwner(null)` is indistinguishable from the initial state
  // and would skip the anon-bucket hydrate.
  hydrated: boolean;
}

// Per-transaction cap surfaced as N/X in the cart UI. Spec calls this the
// "Kinguin API purchase limit per transaction" — exact value depends on the
// merchant tier. 10 is a conservative working value; bump or wire to env when
// the real limit is confirmed.
export const KINGUIN_ORDER_LIMIT = 10;

const STORAGE_PREFIX = 'keyzostore.cart.v2';
// Every cart key prefix this storefront has ever written. Each is purged
// on the first setOwner() call so an upgraded install can't surface a
// stale cart under the new per-user buckets. v1 was a single shared key;
// match is "exact OR starts-with `${prefix}.`/`${prefix}:`" so any
// historical namespaced suffix is caught too.
const LEGACY_KEY_PREFIXES = ['keyzostore.cart.v1'] as const;

function storageKeyFor(ownerId: string | null): string {
  return ownerId ? `${STORAGE_PREFIX}.user.${ownerId}` : `${STORAGE_PREFIX}.anon`;
}

function lineKey(productId: string, offerId: string | undefined): string {
  return `${productId}::${offerId ?? ''}`;
}

function matches(item: CartItem, productId: string, offerId: string | undefined): boolean {
  if (item.productId !== productId) return false;
  // Treat missing offerId on both sides as equal — legacy entries stay
  // dedup-able by productId alone.
  return (item.offerId ?? null) === (offerId ?? null);
}

function loadFromStorage(ownerId: string | null): CartItem[] {
  try {
    const raw = localStorage.getItem(storageKeyFor(ownerId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isCartItem);
  } catch {
    return [];
  }
}

function isCartItem(x: unknown): x is CartItem {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  return (
    typeof o.productId === 'string' &&
    typeof o.name === 'string' &&
    typeof o.priceCents === 'number' &&
    typeof o.qty === 'number' &&
    typeof o.maxQty === 'number'
  );
}

function persist(items: CartItem[], ownerId: string | null) {
  try {
    localStorage.setItem(storageKeyFor(ownerId), JSON.stringify(items));
  } catch {
    // private mode, quota — silently skip; cart will just be in-memory
  }
}

/**
 * Sweep localStorage of every cart bucket this storefront wrote before
 * `STORAGE_PREFIX`. Each legacy prefix is matched as either the bare key
 * or a namespaced suffix (`<prefix>.user.<id>`, `<prefix>:<id>`, `…anon`).
 * Called once per setOwner so the first auth event after upgrade drops
 * the stale rows; subsequent calls are no-ops because nothing matches.
 */
function purgeLegacyCartKeys() {
  if (typeof localStorage === 'undefined') return;
  const toRemove: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      for (const prefix of LEGACY_KEY_PREFIXES) {
        if (k === prefix || k.startsWith(`${prefix}.`) || k.startsWith(`${prefix}:`)) {
          toRemove.push(k);
          break;
        }
      }
    }
    for (const k of toRemove) localStorage.removeItem(k);
  } catch {
    // private mode, quota, or storage disabled — nothing we can do; the
    // legacy entries will live out their TTL on their own.
  }
}

export const useCartStore = defineStore('cart', {
  // Start empty — App.vue calls `setOwner(authUserId)` once auth resolves, at
  // which point the right per-user bucket is hydrated from localStorage.
  state: (): State => ({ items: [], ownerId: null, hydrated: false }),
  getters: {
    count: (s) => s.items.reduce((n, i) => n + i.qty, 0),
    subtotalCents: (s) => s.items.reduce((n, i) => n + i.priceCents * i.qty, 0),
    isEmpty: (s) => s.items.length === 0,
    isAtLimit(): boolean {
      return this.count >= KINGUIN_ORDER_LIMIT;
    },
    remainingSlots(): number {
      return Math.max(0, KINGUIN_ORDER_LIMIT - this.count);
    },
  },
  actions: {
    keyOf(item: Pick<CartItem, 'productId' | 'offerId'>): string {
      return lineKey(item.productId, item.offerId);
    },
    /**
     * Switch the active cart bucket. Called by App.vue whenever the
     * authenticated user changes (login, logout, register, refresh). Items
     * are loaded from `keyzostore.cart.v2.user.<id>` or `…anon`.
     *
     * Also drops the legacy v1 bucket the first time we see it so a stale
     * pre-fix cart can't follow the user.
     */
    setOwner(ownerId: string | null) {
      if (this.hydrated && this.ownerId === ownerId) return;
      this.ownerId = ownerId;
      this.items = loadFromStorage(ownerId);
      this.hydrated = true;
      purgeLegacyCartKeys();
    },
    /**
     * Add `qty` units of a product+offer combination. Items with different
     * offerIds are separate lines. Returns false if the cart is already at
     * the per-transaction limit.
     */
    add(item: Omit<CartItem, 'qty'>, qty = 1): boolean {
      if (qty < 1) return false;
      const remaining = this.remainingSlots;
      if (remaining <= 0) return false;
      const grantedQty = Math.min(qty, remaining);
      const existing = this.items.find((i) => matches(i, item.productId, item.offerId));
      if (existing) {
        existing.qty = Math.min(item.maxQty, existing.qty + grantedQty);
        // Refresh denormalized fields in case price/cover/merchant changed.
        existing.name = item.name;
        existing.priceCents = item.priceCents;
        existing.coverUrl = item.coverUrl;
        existing.imageUrl = item.imageUrl;
        existing.platform = item.platform;
        existing.maxQty = item.maxQty;
        existing.merchantName = item.merchantName;
      } else {
        this.items.push({ ...item, qty: Math.min(grantedQty, item.maxQty) });
      }
      persist(this.items, this.ownerId);
      return true;
    },
    /**
     * Put a removed line back where it was (undo). Respects the same limit
     * as `add`; returns false when the cart can no longer take it.
     */
    restore(item: CartItem, index: number): boolean {
      if (this.items.some((i) => matches(i, item.productId, item.offerId))) return true;
      const otherQty = this.count;
      const qty = Math.min(item.qty, item.maxQty, KINGUIN_ORDER_LIMIT - otherQty);
      if (qty < 1) return false;
      this.items.splice(Math.min(index, this.items.length), 0, { ...item, qty });
      persist(this.items, this.ownerId);
      return true;
    },
    remove(productId: string, offerId?: string) {
      this.items = this.items.filter((i) => !matches(i, productId, offerId));
      persist(this.items, this.ownerId);
    },
    setQty(productId: string, qty: number, offerId?: string) {
      const item = this.items.find((i) => matches(i, productId, offerId));
      if (!item) return;
      const otherQty = this.count - item.qty;
      const max = Math.min(item.maxQty, KINGUIN_ORDER_LIMIT - otherQty);
      item.qty = Math.max(1, Math.min(qty, max));
      persist(this.items, this.ownerId);
    },
    clear() {
      this.items = [];
      persist(this.items, this.ownerId);
    },
  },
});
